/**
 * Balance constants (GDD v0.2). Everything tunable lives here so the simulator and
 * play-tests can adjust numbers in one place.
 */
import type { Doctrine, UnitType } from './types';

export const MINUTES_PER_HOUR = 60;
export const MINUTES_PER_DAY = 24 * 60;

export const BALANCE = {
  /** Engagement resolution cadence (game minutes). */
  engagementTickMinutes: 5,
  /** First hour of any fight deals reduced damage; Steady/Careful may withdraw freely. */
  openingPhaseMinutes: 60,
  openingDamageMultiplier: 0.25,
  /** Troops that can fight per contested hex side; the rest wait in reserve. */
  frontagePerSide: 40,
  armyCap: 150,
  /** Morale loss when a fresh front opens against you. */
  flankShock: 10,
  /** Fraction of casualties that become wounded (rest killed). */
  woundedShare: 0.6,
  /** Each healer returns this many wounded per tick, capped at 10% of current wounded. */
  healPerHealerPerTick: 0.25,
  healCapShare: 0.1,
  /** Out-of-combat, in-supply passive recovery of wounded per hour. */
  passiveRecoveryPerHour: 0.05,
  /** Morale recovery per hour when not engaged (in supply / out of supply). */
  moraleRegenPerHour: 6,
  /** Supplied, halted armies on their own keep, village or watchtower recover faster. */
  garrisonMoraleRegenPerHour: 12,
  moraleDecayOutOfSupplyPerHour: 2,
  /** Out-of-supply decay never takes an idle army below this morale. */
  outOfSupplyMoraleFloor: 55,
  heroMoraleBonus: 10,
  heroDamageMultiplier: 1.1,
  shakenMinutes: 120,
  /** An army whose morale is this far below its retreat threshold routs instead of withdrawing. */
  routMargin: 15,
  /** Share of troops lost (killed) on an ordered retreat and on a rout. */
  retreatLossShare: 0.03,
  routLossShare: 0.08,
  /**
   * Battle-size scaling for morale loss (GDD §7.3 duration targets): morale loss per tick is
   * multiplied by clamp((moraleSizeRef / troopsInEngagement) ^ moraleSizeExponent, min, max),
   * so skirmishes resolve in 1–2 h while field battles take 4–8 h.
   */
  moraleSizeRef: 70,
  moraleSizeExponent: 0,
  moraleSizeMin: 0.5,
  moraleSizeMax: 2.5,
  /** Skirmishes (fewer troops than this in the engagement) break this much faster. */
  skirmishTroops: 50,
  skirmishMoraleMultiplier: 4.5,
  /** In field battles, armies smaller than this lose morale per casualty as if they were this big. */
  fieldMoraleMinSize: 40,
  /** Reserves (troops beyond frontage) steady the line: they count this much towards army size for morale. */
  reserveMoraleWeight: 0.25,
  /** Per-tick morale modifiers in engagements. */
  outnumberedMoraleLoss: 0.3,
  flankedMoraleLossPerSide: 0.3,
  unsuppliedMoraleLoss: 0.2,
  heroWoundedMoraleShock: 15,
  resupplyMoraleBoost: 5,
  /** After leaving an engagement, an army is not re-engaged by Bold neighbours for this long. */
  disengageGraceMinutes: 90,
  /** Ended engagements are kept (for timelines) this long before being pruned. */
  endedEngagementRetentionMinutes: 24 * 60,
  shakenDamageMultiplier: 0.8,
  heroRecoveryMinutes: 4 * 60,
  /**
   * GDD §5 "Defeat: the hero is wounded": a hero whose army is forced off the field by its
   * doctrine threshold after the opening phase is wounded with this chance (routs always wound;
   * ordered withdrawals never do).
   */
  heroWoundOnForcedRetreatChance: 0.35,

  /** Minutes to cross one hex on plains, by unit speed class. */
  minutesPerHex: {
    infantry: 100,
    cavalry: 66,
    hero: 66,
    messenger: 33,
    raven: 11,
  },
  terrainMoveCost: {
    plains: 1,
    forest: 1.5,
    hills: 1.6,
    mountain: Infinity,
    water: Infinity,
    marsh: 2,
  },
  roadMoveMultiplier: 1 / 1.5,
  fordMoveCost: 2,

  unitStats: {
    infantry: { attack: 1.0, hp: 10, cost: 6, recruitMinutes: 60 },
    archers: { attack: 1.1, hp: 8, cost: 8, recruitMinutes: 90 },
    cavalry: { attack: 1.4, hp: 12, cost: 14, recruitMinutes: 120 },
    healers: { attack: 0.2, hp: 6, cost: 10, recruitMinutes: 120 },
    scouts: { attack: 0.3, hp: 6, cost: 5, recruitMinutes: 45 },
  } satisfies Record<UnitType, { attack: number; hp: number; cost: number; recruitMinutes: number }>,
  /** Damage per fighting troop per tick before modifiers. */
  baseDamagePerTroop: 0.12,
  /** Morale points lost per 1% of army lost in a tick. */
  moraleSensitivity: 0.82,
  counterStrong: 1.5,
  counterWeak: 0.7,
  /** Who beats whom: infantry → cavalry → archers → infantry. */
  counters: {
    infantry: 'cavalry',
    cavalry: 'archers',
    archers: 'infantry',
  } as Partial<Record<UnitType, UnitType>>,
  terrainDamage: {
    forestArchers: 0.7,
    forestCavalry: 0.6,
    hillDefenderArchers: 1.3,
    riverAttacker: 0.7,
    fortifiedDefender: 1.25,
  },
  outOfSupplyDamage: 0.75,

  doctrine: {
    bold: { retreatAt: 20, engageAdjacent: true, aidRadius: 3 },
    steady: { retreatAt: 40, engageAdjacent: false, aidRadius: 1 },
    careful: { retreatAt: 60, engageAdjacent: false, aidRadius: 0 },
  } satisfies Record<Doctrine, { retreatAt: number; engageAdjacent: boolean; aidRadius: number }>,
  maxConditionals: 3,

  vision: { hero: 2, army: 1, scouts: 3, watchtower: 3, village: 1, keep: 2, leyNode: 1 },
  ghostLifetimeMinutes: 24 * 60,
  /** Scouts find tracks of enemy armies that passed within this many hexes in the last 12 h. */
  trackRadius: 2,
  trackLifetimeMinutes: 12 * 60,
  /** Teammate pings stay on the map (and AI captains keep answering them) this long. */
  pingLifetimeMinutes: 12 * 60,

  /** Decode time = share of the messenger's remaining travel time, clamped. */
  decodeShare: 0.5,
  decodeMinMinutes: 30,
  decodeMaxMinutes: 4 * 60,
  /** Doctrine changes to armies within this distance of the keep are instant. */
  instantDoctrineRadius: 3,
  ravenManaCost: 2,
  ravenShootDownChance: 0.35,
  /** Archers within this range of a raven may shoot it down (once per army per raven). */
  ravenShootRange: 1,
  /** Enemy armies with scouts catch messengers passing within 1 hex with this chance. */
  scoutInterceptChance: 0.5,
  /** The sender learns a messenger was lost this long after it should have arrived. */
  messengerLostNoticeMinutes: 60,
  /** Messengers route around enemies their team knows about: extra minutes per risky hex. */
  messengerAvoidEnemyHex: 400,
  messengerAvoidAdjacentHex: 120,
  /** Ghosts younger than this still steer messenger routes. */
  messengerAvoidGhostMinutes: 180,

  economy: {
    /** Per player slot of the larger team (both teams get the same — see `parity`). */
    startingFood: 300,
    startingMana: 4,
    foodPerVillagePerHour: 6,
    /** Per player slot of the larger team. */
    foodFromKeepPerHour: 8,
    manaPerLeyNodePerHour: 1,
    channelBonus: 0.5,
    upkeepPerTroopPerHour: 0.06,
    /** Recruit batches take recruitMinutes × (1 + (count − 1) × this). */
    recruitBatchFactor: 0.04,
    /** Supply: friendly territory radius around keeps and around owned villages / ley nodes / towers. */
    keepTerritoryRadius: 2,
    depotTerritoryRadius: 1,
    /** An army this many hexes off the supply network is still supplied. */
    supplyReach: 1,
  },
  /**
   * Unbalanced teams (e.g. 3v5): for every player it lacks, the smaller team starts with an extra
   * hero-less reserve company (`startingArmy` × `troopCompensation`) at its keep, so both sides
   * field the same troops and the same number of armies. Food stocks and keep income are per slot
   * of the larger team for both sides, so economies match. The larger team keeps a small edge in
   * heroes (instant command, hero vision, more relic carriers), offset by extra keep food per
   * missing player per hour and a relic-score multiplier of (largerTeam / ownTeam) ^ exponent for
   * the smaller team (simulation-tuned; see scripts/simulate.ts).
   */
  parity: {
    troopCompensation: 1.2,
    heroGapFoodPerHour: 6,
    underdogScoreExponent: 0.45,
    /** Cap on the underdog relic-score multiplier (2:1 matches need less than the curve gives). */
    underdogScoreMax: 1.26,
  },
  /** Approved late joiners start at their keep with this share of `startingArmy`. */
  lateJoinArmyShare: 0.5,
  build: {
    road: { food: 10, minutes: 60 },
    bridge: { food: 30, minutes: 240 },
    watchtower: { food: 40, minutes: 180 },
    demolishRoad: { minutes: 60 },
    burnBridge: { minutes: 120 },
  },
  spells: {
    rally: { mana: 3, cooldownMinutes: 6 * 60, moraleBonus: 20, range: 4 },
    farSight: { mana: 3, cooldownMinutes: 6 * 60, radius: 3, durationMinutes: 60, range: 8 },
  },

  relics: {
    pulseEveryMinutes: 2 * 60,
    carrierSpeedMultiplier: 0.7,
    pointsPerHourCarried: 1,
    pointsPerHourShrined: 1,
    /** Points multiplier by match phase (expansion / contest / crescendo). */
    phaseMultipliers: [1, 2, 3],
    /** A new relic appears in the middle band on this day (1-based). */
    bonusRelicDay: 3,
  },
  /** Scores within this margin at the end are a tie, broken by scoring relics then villages held. */
  tieMargin: 0.5,
  /** Quiet window: Steady/Careful armies get a defence bonus during their team's 8h window. */
  quietWindowHours: 8,
  quietWindowDefence: 1.2,

  map: {
    /** Approximate hexes per player, before the neutral middle band. */
    hexesPerPlayer: 60,
    minWidth: 14,
    minHeight: 18,
    /** Extra area for the neutral middle band. */
    middleBandFactor: 1.3,
    /** Width / height ratio (teams face north vs south, so maps are taller than wide). */
    aspect: 0.85,
    maxAttempts: 40,
  },
  /** Events kept in `MatchState.events`, and how far back views include events. */
  maxEvents: 500,
  viewEventMinutes: 3 * 24 * 60,
  ghostRemoveAfterMinutes: 30,
  startingArmy: { infantry: 30, archers: 15, cavalry: 8, healers: 5, scouts: 4 } satisfies Record<
    UnitType,
    number
  >,
} as const;

export type Balance = typeof BALANCE;
